import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  CardCVCElement, CardExpiryElement, CardNumberElement, injectStripe,
} from 'react-stripe-elements';
import { useStripeSetupContext } from 'Context/StripeSetupContext';
import Alert from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Alert';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { dataAttrs } from 'Helpers/Testing';
import { FDPrimaryActionButton } from '@facet-wealth/design';

const useStyles = buildUseStyles('AddCreditCardForm', (theme) => ({
  root: {
    minWidth: theme.dimensions.lg,
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  ccForm: {
    width: '100%',
    maxWidth: theme.dimensions.md,
    '& > div': {
      marginBottom: theme.spacing(2.5),
    },
  },
}));

const AddCreditCardForm = ({
  className,
  stripe,
  onSuccess,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {
    stripeErrorMessage,
    processingError,
    handleProcessCreditCard,
    isCreatingStripeToken,
    paymentMethodProcessed,
  } = useStripeSetupContext();

  const stripeElementStyle = {
    base: {
      '::placeholder': {
        color: theme.palette.facet.primaryBlue,
        fontWeight: theme.typography.fontWeightBold,
      },
      fontSize: '16px',
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.facet.primaryBlue,
    },
  };

  const handleSubmit = async () => {
    await handleProcessCreditCard(stripe);
  };

  React.useEffect(() => {
    if (paymentMethodProcessed) {
      onSuccess();
    }
  }, [paymentMethodProcessed]);


  const getErrors = () => {
    const errors = [];
    if (stripeErrorMessage) {
      errors.push(stripeErrorMessage);
    }
    if (processingError?.errors) {
      for (const procError of processingError.errors) {
        errors.push(procError.detail);
      }
    }
    return errors;
  };

  return (
    <Box className={classNames(classes.root, className, 'CreditCardForm')}>
      {getErrors().length > 0 ? (
        <Alert
          variant="filled"
          severity="error"
          className={classes.alert}
          test={'stripe-error-alert'}
        >
          {getErrors().map((errorMessage) => (
            <Typography
              variant={'h5'}
              weight={'bold'}
              color={'inherit'}
              test={'stripe-error-alert-text'}
              key={errorMessage}
            >
              {errorMessage}
            </Typography>
          ))}
        </Alert>
      ) : null}
      <form
        className={classes.ccForm}
        onSubmit={handleSubmit}
        {...dataAttrs('credit card inputs')}
      >

        <CardNumberElement
          className={'StripeCardNumberInput'}
          test="stripe-card-number-input"
          placeholder={'Card Number'}
          style={stripeElementStyle}
        />
        <CardExpiryElement
          className={'StripeCardExpiryInput'}
          test="stripe-card-expiry-input"
          placeholder={'Expiration Date'}
          style={stripeElementStyle}
        />
        <CardCVCElement
          test="stripe-card-cvc-input"
          placeholder={'Security Code'}
          style={stripeElementStyle}
        />
        <FDPrimaryActionButton
          onClick={handleSubmit}
          loading={isCreatingStripeToken}
          isFullWidth
          test={'subscription-setup-submit-payment-button'}
          label={'Save'}
        />
      </form>
    </Box>
  );
};

AddCreditCardForm.defaultProps = {
  className: '',
};

AddCreditCardForm.propTypes = {
  className: PropTypes.string,
  stripe: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default injectStripe(AddCreditCardForm);
